import { CircularProgress, createStyles, makeStyles } from "@material-ui/core"
import clsx from "clsx"
import React, { useState } from "react"
import { useNavigation } from "../../hooks/useNavigation"
import { useSession } from "../../hooks/useSession"
import { addMissingPeriodAtEnd } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import Input from "../atoms/input/Input"
import Logo from "../atoms/Logo"
import { TitledCard } from "../molecules/titledCard/TitledCard"
import { BaseTemplate } from "../templates/BaseTemplate"
import { set } from "js-cookie"

const useStyles = makeStyles((theme) =>
  createStyles({
    login: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      height: "100vh",
      backgroundColor: colors.primary.cloud,
      alignItems: "center",
      justifyContent: "center",
    },
    loginError: {
      color: "red",
    },
    loginButton: {
      width: "100%",
    },
    marginBottom16: {
      marginBottom: theme.spacing(2),
    },
    marginBottom8: {
      marginBottom: theme.spacing(1),
    },
    registerContainer: {
      display: "flex",
      alignItems: "center",
      alignSelf: "center",
    },
    logo: {
      position: "absolute",
      top: theme.spacing(6),
      left: theme.spacing(6),
      zIndex: 1,
    },
    center: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    input: {
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    dontHaveAnAccount: {
      color: colors.neutral.asphalt,
      marginRight: 4,
    },
  })
)

const Login = () => {
  const classes = useStyles()
  const { login } = useSession()
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  )
  const [signingIn, setSigningIn] = useState<boolean>(false)
  const { toForgotPassword } = useNavigation()

  const submit = async () => {
    setEmailError(undefined)
    setPasswordError(undefined)
    let errors = false
    if (!email) {
      setEmailError("Please enter your email to continue")
      errors = true
    }
    if (!password) {
      setPasswordError("Please enter your password to continue")
      errors = true
    }
    if (errors) {
      return
    }
    setSigningIn(true)
    const loginResponse = await login(email, password)
    if (
      loginResponse?.statusCode !== undefined &&
      loginResponse?.statusCode >= 400
    ) {
      setEmailError(" ")
      const message = addMissingPeriodAtEnd(loginResponse.message)
      setPasswordError(`${message} Please try again.`)
      setSigningIn(false)
      return
    }
  }

  return (
    <BaseTemplate>
      <div
        onKeyDown={async (event) => {
          if (event.key === "Enter") {
            await submit()
          }
        }}
      >
        <Logo className={classes.logo} />
        <TitledCard title="Log In">
          <Input
            type="email"
            label="Email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={clsx(classes.input)}
            error={Boolean(emailError)}
            helperText={emailError}
          />
          <Input
            type="password"
            label="Password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={clsx(classes.input)}
            error={Boolean(passwordError)}
            helperText={passwordError}
            enableShowPassword={true}
          />
          <div className={classes.marginBottom16}>
            <PrimaryButton onClick={submit} className={classes.loginButton}>
              {signingIn ? <CircularProgress /> : "Log In"}
            </PrimaryButton>
          </div>
          <div className={clsx(classes.marginBottom8, classes.center)}>
            <GhostButton onClick={toForgotPassword}>
              Forgot Password?
            </GhostButton>
          </div>
        </TitledCard>
      </div>
    </BaseTemplate>
  )
}

export default Login
